import { config } from '../config';

// Esri's tile URL — kept here so we don't duplicate the template across
// the runtime warmup and LeafletViewer.
const SATELLITE_TILE_TEMPLATE =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

// Once warmed we don't redo it for a month. The SW's CacheFirst rule
// already has a 30-day expiration, so anything older than that would be
// evicted anyway.
const WARMED_AT_KEY = 'manglillo.tilesWarmedAt';
const WARM_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// Web Mercator tile math.
function lonToTileX(lon: number, z: number): number {
  return Math.floor(((lon + 180) / 360) * 2 ** z);
}
function latToTileY(lat: number, z: number): number {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** z,
  );
}

/**
 * Walk every Esri tile that covers the site bbox for zooms 14–17 and
 * fetch it once, populating the SW's `esri-world-imagery` runtime cache.
 * Idempotent and silent — failures are ignored, the user keeps using the
 * app at full speed. Only runs while online and at most once per month.
 *
 * Why bbox-only: the visitor's whole interest is the site; pre-warming a
 * larger area would balloon the cache for no benefit. 14–17 covers from
 * "see Margarita" down to "see individual roofs" — Esri stops adding
 * detail at 17 over rural Venezuela.
 */
export function warmEsriTileCache(): void {
  if (!('fetch' in window)) return;
  if (navigator.onLine === false) return;

  try {
    const lastWarmed = parseInt(localStorage.getItem(WARMED_AT_KEY) ?? '0', 10);
    if (Number.isFinite(lastWarmed) && Date.now() - lastWarmed < WARM_TTL_MS) {
      return;
    }
  } catch {
    // localStorage unavailable — just proceed.
  }

  const [[south, west], [north, east]] = config.siteBboxLatLng;

  const urls: string[] = [];
  for (let z = 14; z <= 17; z += 1) {
    const xMin = lonToTileX(west, z);
    const xMax = lonToTileX(east, z);
    // Tile y grows southwards, so the *north* latitude gives the lower y.
    const yMin = latToTileY(north, z);
    const yMax = latToTileY(south, z);
    for (let x = xMin; x <= xMax; x += 1) {
      for (let y = yMin; y <= yMax; y += 1) {
        urls.push(
          SATELLITE_TILE_TEMPLATE.replace('{z}', String(z))
            .replace('{x}', String(x))
            .replace('{y}', String(y)),
        );
      }
    }
  }

  // Defer to idle time so we never compete with the first paint or with
  // tile fetches the visible map is already making.
  const ric =
    (window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 2000));

  ric(
    () => {
      // Fetch in small parallel batches. mode:'no-cors' is safe — Esri's
      // tiles are public, and we only need the SW to see the response
      // pass through, not to read its body.
      const BATCH = 6;
      let i = 0;
      const next = () => {
        if (i >= urls.length) {
          try {
            localStorage.setItem(WARMED_AT_KEY, String(Date.now()));
          } catch {
            // ignore
          }
          return;
        }
        const slice = urls.slice(i, i + BATCH);
        i += BATCH;
        Promise.all(
          slice.map((u) =>
            fetch(u, { mode: 'no-cors', credentials: 'omit' }).catch(() => null),
          ),
        ).then(next);
      };
      next();
    },
    { timeout: 5000 },
  );
}
